import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import AddSupportDocumentModal from '../../components/ui/AddSupportDocumentModal';
import DocumentViewer from '../../components/ui/DocumentViewer';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import AddTranslationModal from '../../components/ui/AddTranslationModal';
import { toApiTranslationAdminStatus } from '../../utils/translationAdminStatus';
import AddFeeTypeModal from '../../components/ui/AddFeeTypeModal';
import AddFAQModal from '../../components/ui/AddFAQModal';
import ActionMenu from '../../components/ui/ActionMenu';
import RichTextEditor from '../../components/ui/RichTextEditor';
import {
  getLicence, updateLicence,
  listDocuments, createDocument, updateDocument, deleteDocument,
  listTranslations, createTranslation, updateTranslation, deleteTranslation,
  listFAQs, createFAQ, updateFAQ, deleteFAQ,
  getFees, updateFees,
  getTerms, updateTerms,
  uploadFileAndGetUrl
} from '../../services/licences';

// Legacy organization types (kept for backward compatibility)
const LEGACY_ORGANIZATION_TYPES = [

];

// Organization types mapping - all 14 types
const ORGANIZATION_TYPES = [
  { key: 'academiaNonCommercial', label: 'Academia – Non-commercial', shortLabel: 'Academia (Non-commercial)' },
  { key: 'academiaCommerciallyFunded', label: 'Academia – Commercially funded', shortLabel: 'Academia (Commercial)' },
  { key: 'publiclyFundedHealthcareProvider', label: 'Publicly funded healthcare provider', shortLabel: 'Public Healthcare' },
  { key: 'privatelyFundedHealthcareProvider', label: 'Privately funded healthcare provider', shortLabel: 'Private Healthcare' },
  { key: 'publisherEducationalUse', label: 'Publisher – Educational use', shortLabel: 'Publisher (Educational)' },
  { key: 'individualClinicalPracticeNotForProfit', label: 'Individual Clinical Practice – Not for profit', shortLabel: 'Clinical Practice (Non-profit)' },
  { key: 'individualClinicalPracticeForProfit', label: 'Individual Clinical Practice – For profit', shortLabel: 'Clinical Practice (For profit)' },
  { key: 'nutraceuticalCompany', label: 'Nutraceutical Company', shortLabel: 'Nutraceutical' },
  { key: 'contractResearchOrganisation', label: 'Contract Research Organisation (CRO)', shortLabel: 'CRO' },
  { key: 'commercialResearchPharma', label: 'Commercial Research – Pharma or medical device company', shortLabel: 'Commercial Research' },
  // { key: 'itCompanyAccessOnly', label: 'IT Company – Access only (No sublicensing)', shortLabel: 'IT Company (Access only)' },
  // { key: 'itCompanyFullRights', label: 'IT Company – Full rights', shortLabel: 'IT Company (Full rights)' },
  { key: 'translatorTranslationCompany', label: 'Translator / Translation Company', shortLabel: 'Translator/Translation' },

];

// Combine all columns for display
const ALL_ORGANIZATION_TYPES = [ ...ORGANIZATION_TYPES];

const EditLicence = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('Product Details');
  const [loading, setLoading] = useState(true);
  const [licenceData, setLicenceData] = useState(null);
  const [selectedTag, setSelectedTag] = useState('New Translations');
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddTranslationModalOpen, setIsAddTranslationModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [translationToDelete, setTranslationToDelete] = useState(null);
  const [isAddFeeTypeModalOpen, setIsAddFeeTypeModalOpen] = useState(false);
  const [isAddFAQModalOpen, setIsAddFAQModalOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [termsContent, setTermsContent] = useState('');
  const [descriptionContent, setDescriptionContent] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [permissionsContent, setPermissionsContent] = useState('');
  const [restrictionsContent, setRestrictionsContent] = useState('');
  const [ecoaNotesContent, setEcoaNotesContent] = useState('');
  const [otherInfoContent, setOtherInfoContent] = useState('');
  const [keyReferencesContent, setKeyReferencesContent] = useState('');
  const [externalLinksContent, setExternalLinksContent] = useState('');
  const [backgroundContent, setBackgroundContent] = useState('');
  const [scoringSystemContent, setScoringSystemContent] = useState('');
  const [developmentMethodologyContent, setDevelopmentMethodologyContent] = useState('');
  const [exampleStudiesContent, setExampleStudiesContent] = useState('');
  const [translations, setTranslations] = useState([]);
  const [faqData, setFaqData] = useState([]);
  const [feeData, setFeeData] = useState({ feeTypes: [], licenceFee: 0, translationFeeTotal: 0, supportDocsFeeTotal: 0, totalPrice: 0, feeItems: [] });

  // Load all data on mount
  useEffect(() => {
    if (id) {
      loadAllData();
    }
  }, [id]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [licenceRes, docsRes, transRes, faqsRes, feesRes, termsRes] = await Promise.all([
        getLicence(id).catch(() => ({ data: null })),
        listDocuments(id).catch(() => ({ data: [] })),
        listTranslations(id).catch(() => ({ data: [] })),
        listFAQs(id).catch(() => ({ data: [] })),
        getFees(id).catch(() => ({ data: { feeTypes: [], licenceFee: 0, translationFeeTotal: 0, supportDocsFeeTotal: 0, totalPrice: 0, feeItems: [] } })),
        getTerms(id).catch(() => ({ data: null }))
      ]);

      if (licenceRes.data) {
        setLicenceData(licenceRes.data);
        setOrganizationType(licenceRes.data.organizationType || '');
        setDescriptionContent(licenceRes.data.description || '');
      }
      if (docsRes.data) {
        // Normalise document shape for viewer (expects `url`)
        const normalisedDocs = docsRes.data.map(doc => ({
          ...doc,
          url: doc.url || doc.fileUrl || ''
        }));
        setDocuments(normalisedDocs);
      }
      if (transRes.data) {
        setTranslations(transRes.data);
      }
      if (faqsRes.data) {
        setFaqData(faqsRes.data);
      }
      if (feesRes.data) {
        setFeeData(feesRes.data);
      }
      if (termsRes.data) {
        setTermsContent(termsRes.data.termsContent || '');
        setPermissionsContent(termsRes.data.permissionsToUse || '');
        setRestrictionsContent(termsRes.data.restrictions || '');
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      alert('Failed to load licence data');
    } finally {
      setLoading(false);
    }
  };
  //   { 
  //     id: 3, 
  //     country: 'Hesse', 
  //     language: 'Ewe', 
  //     status: 'pending', 
  //     tag: 'Translate Themselves',
  //     fee: '£30', 
  //     note: '', 
  //     dateCreated: '03/15/2023', 
  //     lastAction: '03/15/2023', 
  //     lastActionBy: 'Floyd Miles',
  //     avatar: 'FM'
  //   },
  //   { 
  //     id: 4, 
  //     country: 'Hawaii', 
  //     language: 'Spanish', 
  //     status: 'approved', 
  //     tag: 'IHO Commissioned',
  //     fee: '£0.00', 
  //     note: '', 
  //     dateCreated: '01/05/2026', 
  //     lastAction: '01/05/2026', 
  //     lastActionBy: 'Robert Fox',
  //     avatar: 'RF'
  //   },
  //   { 
  //     id: 5, 
  //     country: 'Gabon', 
  //     language: 'Avaric', 
  //     status: 'pending', 
  //     tag: 'Translate Themselves',
  //     fee: '£55', 
  //     note: '', 
  //     dateCreated: '01/05/2026', 
  //     lastAction: '01/05/2026', 
  //     lastActionBy: 'Bessie Cooper',
  //     avatar: 'BC'
  //   },
  //   { 
  //     id: 6, 
  //     country: 'Duchy of Parma', 
  //     language: 'Uighur', 
  //     status: 'rejected', 
  //     tag: 'Form Library',
  //     fee: '£80', 
  //     note: 'Looking forward to our pr...', 
  //     dateCreated: '11/22/2025', 
  //     lastAction: '11/22/2025', 
  //     lastActionBy: 'Albert Flores',
  //     avatar: 'AF'
  //   },
  //   { 
  //     id: 7, 
  //     country: 'Bangladesh', 
  //     language: 'Dutch', 
  //     status: 'approved', 
  //     tag: 'IHO Commissioned',
  //     fee: '£25', 
  //     note: 'March 15, 2023 is a signific...', 
  //     dateCreated: '03/15/2023', 
  //     lastAction: '03/15/2023', 
  //     lastActionBy: 'Darrell Steward',
  //     avatar: 'DS'
  //   },
  //   { 
  //     id: 8, 
  //     country: 'Fiji', 
  //     language: 'Vietnamese', 
  //     status: 'pending', 
  //     tag: 'Translate Themselves',
  //     fee: '£0.00', 
  //     note: '', 
  //     dateCreated: '01/05/2026', 
  //     lastAction: '01/05/2026', 
  //     lastActionBy: 'Brooklyn Sim...',
  //     avatar: 'BS'
  //   },
  //   { 
  //     id: 9, 
  //     country: 'El Salvador', 
  //     language: 'French', 
  //     status: 'approved', 
  //     tag: 'IHO Commissioned',
  //     fee: '£35', 
  //     note: '', 
  //     dateCreated: '01/05/2026', 
  //     lastAction: '01/05/2026', 
  //     lastActionBy: 'Kathryn Murp...',
  //     avatar: 'KM'
  //   },
  //   { 
  //     id: 10, 
  //     country: 'Cameroon', 
  //     language: 'Bambara', 
  //     status: 'rejected', 
  //     tag: 'Form Library',
  //     fee: '£50', 
  //     note: "Let's ensure we're on track...", 
  //     dateCreated: '01/05/2026', 
  //     lastAction: '01/05/2026', 
  //     lastActionBy: 'Jenny Wilson',
  //     avatar: 'JW'
  //   },
  //   { 
  //     id: 11, 
  //     country: 'Czechia', 
  //     language: 'Southern Sotho', 
  //     status: 'approved', 
  //     tag: 'IHO Commissioned',
  //     fee: '£100', 
  //     note: '', 
  //     dateCreated: '01/05/2026', 
  //     lastAction: '01/05/2026', 
  //     lastActionBy: 'Leslie Alexand...',
  //     avatar: 'LA'
  //   }
  // ]);
  const [documents, setDocuments] = useState([]);
  //   { id: 2, name: 'Spine Health Assessmen...', detail: 'It provides valuable insights into ind...', fee: '£75', dateCreated: '11/22/2025', lastAction: '11/22/2025', user: 'Ralph Edwards', avatar: 'RE' },
  //   { id: 3, name: 'Workplace Wellbeing Ev...', detail: 'This evaluation explores the social...', fee: '£30', dateCreated: '03/15/2023', lastAction: '03/15/2023', user: 'Floyd Miles', avatar: 'FM' },
  //   { id: 4, name: 'Team Satisfaction Analy...', detail: 'It is essential for measuring workpla...', fee: '£0.00', dateCreated: '01/05/2026', lastAction: '01/05/2026', user: 'Robert Fox', avatar: 'RF' },
  //   { id: 5, name: 'Workplace Contentment...', detail: 'This survey delivers important insig...', fee: '£55', dateCreated: '01/05/2026', lastAction: '01/05/2026', user: 'Bessie Cooper', avatar: 'BC' },
  //   { id: 6, name: 'Posture Awareness Ques...', detail: 'This questionnaire assesses individ...', fee: '£80', dateCreated: '11/22/2025', lastAction: '11/22/2025', user: 'Albert Flores', avatar: 'AF' },
  //   { id: 7, name: 'Job Environment Impact...', detail: 'This questionnaire investigates the...', fee: '£25', dateCreated: '03/15/2023', lastAction: '03/15/2023', user: 'Darrell Steward', avatar: 'DS' },
  //   { id: 8, name: 'Employee Engagement...', detail: 'This feedback is crucial for underst...', fee: '£0.00', dateCreated: '01/05/2026', lastAction: '01/05/2026', user: 'Brooklyn Sim...', avatar: 'BS' },
  //   { id: 9, name: 'Work Satisfaction Index', detail: 'This survey serves as a key indicato...', fee: '£35', dateCreated: '01/05/2026', lastAction: '01/05/2026', user: 'Kathryn Murp...', avatar: 'KM' },
  //   { id: 10, name: 'Career Fulfillment Survey', detail: 'This index is important for evaluatin...', fee: '£50', dateCreated: '01/05/2026', lastAction: '01/05/2026', user: 'Jenny Wilson', avatar: 'JW' },
  //   { id: 11, name: 'Job Enjoyment Assessm...', detail: 'This survey effectively measures jo...', fee: '£100', dateCreated: '01/05/2026', lastAction: '01/05/2026', user: 'Leslie Alexand...', avatar: 'LA' },
  // ]);

  const handleAddDocument = async (newDoc) => {
    try {
      let fileUrl = newDoc.fileUrl;
      if (newDoc.file) {
        fileUrl = await uploadFileAndGetUrl(newDoc.file);
      }

      if (editingDocument) {
        // Update existing document
        const updated = await updateDocument(editingDocument._id, {
          name: newDoc.name,
          detail: newDoc.detail,
          fee: parseFloat(newDoc.fee) || 0,
          fileUrl,
          isLocked: newDoc.isLocked !== undefined ? newDoc.isLocked : false
        });
        setDocuments(documents.map(doc => {
          if (doc._id === editingDocument._id) {
            const updatedDoc = updated.data;
            return {
              ...updatedDoc,
              url: updatedDoc.url || updatedDoc.fileUrl || ''
            };
          }
          return doc;
        }));
        setEditingDocument(null);
      } else {
        // Add new document
        const created = await createDocument(id, {
          name: newDoc.name,
          detail: newDoc.detail,
          fee: parseFloat(newDoc.fee) || 0,
          fileUrl,
          isLocked: newDoc.isLocked || false
        });
        const createdDoc = created.data;
        setDocuments([
          ...documents,
          {
            ...createdDoc,
            url: createdDoc.url || createdDoc.fileUrl || ''
          }
        ]);
      }
      setIsAddDocumentModalOpen(false);
    } catch (err) {
      console.error('Failed to save document:', err);
      alert(`Failed to save document: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDocumentAction = (action, docId) => {
    const doc = documents.find(d => d._id === docId || d.id === docId);
    
    if (action === 'delete') {
      setDocumentToDelete(doc);
      setIsDeleteModalOpen(true);
    } else if (action === 'edit') {
      setEditingDocument(doc);
      setIsAddDocumentModalOpen(true);
    } else if (action === 'view') {
      setSelectedDocument(doc);
      setIsDocumentViewerOpen(true);
    }
  };

  const handleDeleteDocumentConfirm = async () => {
    if (!documentToDelete) return;
    try {
      await deleteDocument(documentToDelete._id || documentToDelete.id);
      setDocuments(documents.filter(d => (d._id || d.id) !== (documentToDelete._id || documentToDelete.id)));
      setIsDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (err) {
      console.error('Failed to delete document:', err);
      alert(`Failed to delete document: ${err.message || 'Unknown error'}`);
    }
  };

  const handleCloseModal = () => {
    setIsAddDocumentModalOpen(false);
    setEditingDocument(null);
  };

  const handleAddTranslation = async (newTranslation) => {
    try {
      let translationFileUrl = newTranslation.translationFileUrl;
      let methodologyFileUrl = newTranslation.methodologyFileUrl;
      
      if (newTranslation.translationFile) {
        translationFileUrl = await uploadFileAndGetUrl(newTranslation.translationFile);
      }
      if (newTranslation.methodologyFile) {
        methodologyFileUrl = await uploadFileAndGetUrl(newTranslation.methodologyFile);
      }

      if (editingTranslation) {
        // Update existing translation
        const updated = await updateTranslation(editingTranslation._id, {
          coaName: newTranslation.coaName?.trim() || undefined,
          localeTag: newTranslation.localeTag || '',
          country: newTranslation.country,
          language: newTranslation.language,
          adminStatus: toApiTranslationAdminStatus(newTranslation.status),
          tag: newTranslation.tag || 'IHO Commissioned',
          fee: parseFloat(newTranslation.fee) || 0,
          note: newTranslation.note || '',
          translationFileUrl,
          methodologyFileUrl,
          territory: newTranslation.territory || '',
          continent: newTranslation.continent || '',
          translationDate: newTranslation.translationDate || '',
          author: newTranslation.author || '',
          fundedBy: newTranslation.fundedBy || '',
          method: newTranslation.method || '',
          isLocked: newTranslation.isLocked !== undefined ? newTranslation.isLocked : false,
          isSourceVersion: !!newTranslation.isSourceVersion,
          certified: !!newTranslation.certified
        });
        setTranslations(translations.map(trans => trans._id === editingTranslation._id ? updated.data : trans));
        setEditingTranslation(null);
      } else {
        // Add new translation
        const created = await createTranslation(id, {
          coaName: newTranslation.coaName?.trim() || undefined,
          localeTag: newTranslation.localeTag || '',
          country: newTranslation.country,
          language: newTranslation.language,
          adminStatus: toApiTranslationAdminStatus(newTranslation.status),
          tag: newTranslation.tag || 'IHO Commissioned',
          fee: parseFloat(newTranslation.fee) || 0,
          note: newTranslation.note || '',
          translationFileUrl,
          methodologyFileUrl,
          territory: newTranslation.territory || '',
          continent: newTranslation.continent || '',
          translationDate: newTranslation.translationDate || '',
          author: newTranslation.author || '',
          fundedBy: newTranslation.fundedBy || '',
          method: newTranslation.method || '',
          isLocked: newTranslation.isLocked || false,
          isSourceVersion: !!newTranslation.isSourceVersion,
          certified: !!newTranslation.certified
        });
        setTranslations([...translations, created.data]);
      }
      setIsAddTranslationModalOpen(false);
    } catch (err) {
      console.error('Failed to save translation:', err);
      alert(`Failed to save translation: ${err.message || 'Unknown error'}`);
    }
  };

  const handleTranslationAction = (action, transId) => {
    const trans = translations.find(t => t._id === transId || t.id === transId);
    
    if (action === 'delete') {
      setTranslationToDelete(trans);
      setIsDeleteModalOpen(true);
    } else if (action === 'edit') {
      setEditingTranslation(trans);
      setIsAddTranslationModalOpen(true);
    } else if (action === 'view') {
      // Convert translation to document format for viewer
      const translationDoc = {
        name: `${trans.country} - ${trans.language}`,
        detail: `Translation Status: ${trans.status}`,
        fee: trans.fee,
        dateCreated: trans.dateCreated,
        user: trans.lastActionBy,
        url: trans.translationFileUrl || 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
      };
      setSelectedDocument(translationDoc);
      setIsDocumentViewerOpen(true);
    }
  };

  const handleDeleteTranslationConfirm = async () => {
    if (translationToDelete) {
      try {
        await deleteTranslation(translationToDelete._id || translationToDelete.id);
        setTranslations(translations.filter(trans => (trans._id || trans.id) !== (translationToDelete._id || translationToDelete.id)));
        setTranslationToDelete(null);
        setIsDeleteModalOpen(false);
      } catch (err) {
        console.error('Failed to delete translation:', err);
        alert(`Failed to delete translation: ${err.message || 'Unknown error'}`);
      }
    } else if (documentToDelete) {
      await handleDeleteDocumentConfirm();
    }
  };

  const handleCloseTranslationModal = () => {
    setIsAddTranslationModalOpen(false);
    setEditingTranslation(null);
  };

  const handleFeeChange = (feeType, category, value) => {
    setFeeData({
      ...feeData,
      [feeType]: {
        ...feeData[feeType],
        [category]: value
      }
    });
  };

  const handleSaveFeeTypes = async (newFeeTypes) => {
    try {
      // Update feeData with new fee types
      const updatedFeeData = { ...feeData };
      if (!updatedFeeData.feeTypes) {
        updatedFeeData.feeTypes = [];
      }
      newFeeTypes.forEach(feeType => {
        if (!updatedFeeData.feeTypes.find(ft => ft.name === feeType.name)) {
          const feeRow = { name: feeType.name };
          ALL_ORGANIZATION_TYPES.forEach(orgType => {
            feeRow[orgType.key] = feeType[orgType.key] != null ? Number(feeType[orgType.key]) : 0;
          });
          updatedFeeData.feeTypes.push(feeRow);
        }
      });
      
      // Save to API
      await updateFees(id, updatedFeeData);
      setFeeData(updatedFeeData);
      alert('Fees saved successfully');
    } catch (err) {
      console.error('Failed to save fees:', err);
      alert(`Failed to save fees: ${err.message || 'Unknown error'}`);
    }
  };

  const handleSaveFees = async () => {
    try {
      await updateFees(id, feeData);
      alert('Fees saved successfully');
    } catch (err) {
      console.error('Failed to save fees:', err);
      alert(`Failed to save fees: ${err.message || 'Unknown error'}`);
    }
  };

  const handleSaveTerms = async () => {
    try {
      await updateTerms(id, {
        termsContent,
        permissionsToUse: permissionsContent,
        restrictions: restrictionsContent
      });
      alert('Terms & Conditions saved successfully');
    } catch (err) {
      console.error('Failed to save terms:', err);
      alert(`Failed to save terms: ${err.message || 'Unknown error'}`);
    }
  };

  const handleAddFAQ = async (newFAQ) => {
    const orderVal =
      newFAQ.order !== undefined && newFAQ.order !== null
        ? Number(newFAQ.order)
        : faqData.reduce((m, f) => Math.max(m, Number(f.order) || 0), -1) + 1;
    try {
      if (editingFAQ) {
        // Update existing FAQ
        const updated = await updateFAQ(editingFAQ._id, {
          question: newFAQ.question,
          answer: newFAQ.answer,
          isList: newFAQ.isList || false,
          order: orderVal
        });
        setFaqData(faqData.map(faq => faq._id === editingFAQ._id ? updated.data : faq));
        setEditingFAQ(null);
      } else {
        // Add new FAQ
        const created = await createFAQ(id, {
          question: newFAQ.question,
          answer: newFAQ.answer,
          isList: newFAQ.isList || false,
          order: orderVal
        });
        setFaqData([...faqData, created.data]);
      }
      setIsAddFAQModalOpen(false);
    } catch (err) {
      console.error('Failed to save FAQ:', err);
      alert(`Failed to save FAQ: ${err.message || 'Unknown error'}`);
    }
  };

  const handleFAQAction = (action, faqId) => {
    const faq = faqData.find(f => f._id === faqId || f.id === faqId);
    
    if (action === 'edit') {
      setEditingFAQ(faq);
      setIsAddFAQModalOpen(true);
    } else if (action === 'delete') {
      setFaqToDelete(faq);
      setIsDeleteModalOpen(true);
    }
  };

  // FAQ Menu Items
  const faqMenuItems = [
    {
      label: 'Edit FAQ',
      action: 'edit',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M14.1667 2.49998C14.3856 2.28113 14.6454 2.10744 14.9314 1.98899C15.2173 1.87054 15.5238 1.80957 15.8334 1.80957C16.1429 1.80957 16.4494 1.87054 16.7353 1.98899C17.0213 2.10744 17.2811 2.28113 17.5 2.49998C17.7189 2.71884 17.8926 2.97863 18.011 3.2646C18.1295 3.55057 18.1904 3.85706 18.1904 4.16665C18.1904 4.47624 18.1295 4.78273 18.011 5.0687C17.8926 5.35467 17.7189 5.61446 17.5 5.83332L6.25002 17.0833L1.66669 18.3333L2.91669 13.75L14.1667 2.49998Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      danger: false
    },
    {
      label: 'Delete',
      action: 'delete',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M2.5 5H4.16667H17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15.8333 4.99999V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83332C5.39129 18.3333 4.96737 18.1577 4.65481 17.8452C4.34225 17.5326 4.16666 17.1087 4.16666 16.6667V4.99999M6.66666 4.99999V3.33332C6.66666 2.89129 6.84225 2.46737 7.15481 2.15481C7.46737 1.84225 7.89129 1.66666 8.33332 1.66666H11.6667C12.1087 1.66666 12.5326 1.84225 12.8452 2.15481C13.1577 2.46737 13.3333 2.89129 13.3333 3.33332V4.99999" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8.33334 9.16666V14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11.6667 9.16666V14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      danger: true
    }
  ];

  const handleDeleteFAQConfirm = async () => {
    if (faqToDelete) {
      try {
        await deleteFAQ(faqToDelete._id || faqToDelete.id);
        setFaqData(faqData.filter(faq => (faq._id || faq.id) !== (faqToDelete._id || faqToDelete.id)));
        setFaqToDelete(null);
        setIsDeleteModalOpen(false);
      } catch (err) {
        console.error('Failed to delete FAQ:', err);
        alert(`Failed to delete FAQ: ${err.message || 'Unknown error'}`);
      }
    }
  };

  // Unified delete handler for the modal
  const handleDeleteConfirm = async () => {
    if (documentToDelete) {
      await handleDeleteDocumentConfirm();
    } else if (translationToDelete) {
      await handleDeleteTranslationConfirm();
    } else if (faqToDelete) {
      await handleDeleteFAQConfirm();
    }
  };

  const handleCloseFAQModal = () => {
    setIsAddFAQModalOpen(false);
    setEditingFAQ(null);
  };

  const tabs = [
    'Product Details',
    'Documents',
    'Translations',
    'Fee',
    "FAQ's",
    'Terms & Conditions'
  ];

  return (
    <>
      <AddSupportDocumentModal
        isOpen={isAddDocumentModalOpen}
        onClose={handleCloseModal}
        onAdd={handleAddDocument}
        editingDocument={editingDocument}
      />

      <DocumentViewer
        isOpen={isDocumentViewerOpen}
        onClose={() => setIsDocumentViewerOpen(false)}
        document={selectedDocument}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDocumentToDelete(null);
          setTranslationToDelete(null);
          setFaqToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={documentToDelete ? "Delete Document" : translationToDelete ? "Delete Translation" : "Delete FAQ"}
        message={`Are you sure you want to delete this ${documentToDelete ? 'document' : translationToDelete ? 'translation' : 'FAQ'}? This action cannot be undone.`}
        itemName={documentToDelete?.name || (translationToDelete ? `${translationToDelete.country} - ${translationToDelete.language}` : faqToDelete?.question || '')}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <AddTranslationModal
        isOpen={isAddTranslationModalOpen}
        onClose={handleCloseTranslationModal}
        onAdd={handleAddTranslation}
        editingTranslation={editingTranslation}
        currentCoaName={
          typeof licenceData?.coaId === 'object' && licenceData?.coaId?.name
            ? licenceData.coaId.name
            : ''
        }
      />

      <AddFeeTypeModal
        isOpen={isAddFeeTypeModalOpen}
        onClose={() => setIsAddFeeTypeModalOpen(false)}
        onSave={handleSaveFeeTypes}
        existingFeeTypes={Object.keys(feeData).map((name, index) => ({ id: index + 1, name }))}
      />

      <AddFAQModal
        isOpen={isAddFAQModalOpen}
        onClose={handleCloseFAQModal}
        onAdd={handleAddFAQ}
        editingFAQ={editingFAQ}
        defaultOrder={faqData.reduce((m, f) => Math.max(m, Number(f.order) || 0), -1) + 1}
      />
      
    <div className="max-w-7xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-1">
          Edit Licence
        </h1>
        <p className="text-sm text-gray-500">
          Information under this section is only available to users once they have signed in.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-8 bg-[#F2F2F2] rounded-lg p-1">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-all rounded-lg ${
                activeTab === tab
                  ? 'bg-[#F0FFF2] text-emerald-600'
                  : 'text-gray-600 hover:bg-white/50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-10">
        {activeTab === 'Product Details' && (
          <>
        <h2 className="text-lg font-semibold text-gray-900 mb-8">Product Details</h2>

        {/* Licence Logo */}
        <div className="mb-10">
          <label className="block text-sm font-medium text-gray-700 mb-3">Licence Logo</label>
          <div className="flex items-start gap-4">
            {/* Logo Preview */}
            <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white relative flex-shrink-0">
              <div className="text-center">
                <div className="text-3xl font-bold leading-none mb-1">Q</div>
                <div className="text-xs font-semibold px-2 py-0.5 bg-blue-700 rounded">BBQ</div>
              </div>
            </div>

            {/* Upload Buttons */}
            <div>
              <div className="flex gap-2 mb-2">
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors border border-emerald-200">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 10.6667V4M8 4L5.33333 6.66667M8 4L10.6667 6.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.66667 10.6667V12C2.66667 12.3536 2.80714 12.6928 3.05719 12.9428C3.30724 13.1929 3.64638 13.3333 4 13.3333H12C12.3536 13.3333 12.6928 13.1929 12.9428 12.9428C13.1929 12.6928 13.3333 12.3536 13.3333 12V10.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Upload new image
                </button>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors border border-gray-300">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 4H14M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333M3.33333 4L4 13.3333C4 13.687 4.14048 14.026 4.39052 14.2761C4.64057 14.5262 4.97971 14.6667 5.33333 14.6667H10.6667C11.0203 14.6667 11.3594 14.5262 11.6095 14.2761C11.8595 14.026 12 13.687 12 13.3333L12.6667 4M6 4V2.66667C6 2.48986 6.07024 2.32029 6.19526 2.19526C6.32029 2.07024 6.48986 2 6.66667 2H9.33333C9.51014 2 9.67971 2.07024 9.80474 2.19526C9.92976 2.32029 10 2.48986 10 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Remove
                </button>
              </div>
              <p className="text-xs text-gray-500">System support PNG and JPG, under 10MB</p>
            </div>
          </div>
        </div>

        {/* Name, Code, Disease/Condition */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input
              type="text"
              defaultValue="Recurrent Urinary Tract Infection Symptom Sca"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Code</label>
            <input
              type="text"
              defaultValue="RUTISS"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Disease/Condition</label>
            <input
              type="text"
              defaultValue="Urology"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
          </div>
        </div>

        {/* Description */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <RichTextEditor
            value={descriptionContent}
            onChange={setDescriptionContent}
            placeholder="Enter description..."
          />
        

        {/* Organization Type */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">Organisation Type</label>
          <div className="relative">
            <select
              value={organizationType}
              onChange={(e) => setOrganizationType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
            >
              <option value="">Select organisation type</option>
              <option value="Academic research - funded by a pharmaceutical organisation">Academic research - funded by a pharmaceutical organisation</option>
              <option value="Academic research - publicly funded">Academic research - publicly funded</option>
              <option value="Private healthcare provider">Private healthcare provider</option>
              <option value="Public healthcare provider">Public healthcare provider</option>
              <option value="Pharmaceutical company">Pharmaceutical company</option>
              <option value="Biotechnology company">Biotechnology company</option>
              <option value="Contract research organization (CRO)">Contract research organisation (CRO)</option>
              <option value="Medical device company">Medical device company</option>
              <option value="Publisher - educational">Publisher - educational</option>
              <option value="Publisher - commercial">Publisher - commercial</option>
              <option value="Non-profit organization">Non-profit organisation</option>
              <option value="Government agency">Government agency</option>
              <option value="Other">Other</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
</div>

        {/* Copyright & Usage Rights */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Copyright & Usage Rights</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Authors</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Copyright</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Permissions to use / Licence */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions to use / Licence</label>
            <textarea
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent resize-none"
              placeholder=""
            />
          </div>

          {/* Titled Restrictions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titled Restrictions</label>
            <textarea
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent resize-none"
              placeholder=""
            />
          </div>
        </div>

        {/* Details */}
        <div className="mb-10">
          <h3 className="text-base font-semibold text-gray-900 mb-6">Details</h3>
          
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type of COA</label>
              <div className="relative">
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white">
                  <option value="">Select</option>
                  <option value="Patient-Reported Outcome Measures (PROMs)">Patient-Reported Outcome Measures (PROMs) </option>
<option value="Clinician-Reported Outcome Measures (ClinROs)">Clinician-Reported Outcome Measures (ClinROs) </option>
<option value="Observer-Reported Outcome Measures (ObsROs)">Observer-Reported Outcome Measures (ObsROs) </option>
<option value="Performance Outcome Measures (PerfOs)">Performance Outcome Measures (PerfOs) </option>
<option value="Patient Diary (PD)">Patient Diary (PD) </option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
              <div className="relative border border-gray-300 rounded-lg">
                <select 
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white opacity-0 cursor-pointer relative z-10 border-0"
                >
                  <option value="New Translations">New Translations Available</option>
                  <option value="updated">Updated</option>
                  <option value="Frequently Requested">Frequently Requested</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-20">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {/* Dynamic badge overlay */}
                <div className="absolute inset-0 flex items-center px-3 pointer-events-none">
                  {selectedTag === 'New Translations' && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded bg-lime-500 text-white text-xs font-medium">
                      New Translations Available
                    </span>
                  )}
                  {selectedTag === 'updated' && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded bg-blue-500 text-white text-xs font-medium">
                      Updated
                    </span>
                  )}
                  {selectedTag === 'Frequently Requested' && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded bg-orange-500 text-white text-xs font-medium">
                      Frequently Requested
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Age Group</label>
              <div className="relative">
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white">
                  <option value="">Select</option>
                  <option>Children (0-12)</option>
                  <option>Teens (13-17)</option>
                  <option>Adults (18+)</option>
                  <option>Seniors (65+)</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Second row */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Respondents</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
              <div className="relative">
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white">
                  <option value="">Select</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                  <option>All</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Item</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Third row */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recall Period</label>
              <div className="relative">
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white">
                  <option value="">Select</option>
                  <option>24 hours</option>
                  <option>1 week</option>
                  <option>2 weeks</option>
                  <option>1 month</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Response scales</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Domains / concepts measured</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Fourth row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Administration time</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Administration Modes</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Notes for eCOA reproductions and Other Information */}
        <div className="grid grid-cols-2 gap-6 mb-10">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes for eCOA reproductions</label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              {/* Toolbar */}
              <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
                <button className="p-1 hover:bg-gray-200 rounded">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button className="p-1 hover:bg-gray-200 rounded">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                  <option>Normal text</option>
                </select>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                  <option>Align</option>
                </select>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
                <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
                <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
                <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <button className="p-1 hover:bg-gray-200 rounded">•</button>
                <button className="p-1 hover:bg-gray-200 rounded">1.</button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
              </div>
              <textarea
                rows={4}
                className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
                placeholder=""
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional details</label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              {/* Toolbar */}
              <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
                <button className="p-1 hover:bg-gray-200 rounded">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button className="p-1 hover:bg-gray-200 rounded">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                  <option>Normal text</option>
                </select>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                  <option>Align</option>
                </select>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
                <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
                <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
                <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <button className="p-1 hover:bg-gray-200 rounded">•</button>
                <button className="p-1 hover:bg-gray-200 rounded">1.</button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
              </div>
              <textarea
                rows={4}
                className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* References & External Resources */}
        <div className="mb-10">
          <h3 className="text-base font-semibold text-gray-900 mb-6">References & External Resources</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Original Development Language</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
          </div>

          {/* Key references and Useful external links */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Key references</label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
                  <button className="p-1 hover:bg-gray-200 rounded">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="p-1 hover:bg-gray-200 rounded">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                    <option>Normal text</option>
                  </select>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                    <option>Align</option>
                  </select>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <button className="p-1 hover:bg-gray-200 rounded">•</button>
                  <button className="p-1 hover:bg-gray-200 rounded">1.</button>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
                </div>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
                  placeholder=""
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Useful external links</label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
                  <button className="p-1 hover:bg-gray-200 rounded">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="p-1 hover:bg-gray-200 rounded">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                    <option>Normal text</option>
                  </select>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                    <option>Align</option>
                  </select>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
                  <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <button className="p-1 hover:bg-gray-200 rounded">•</button>
                  <button className="p-1 hover:bg-gray-200 rounded">1.</button>
                  <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
                </div>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
                  placeholder=""
                />
              </div>
            </div>
          </div>
        </div>

        {/* Background */}
        <div className="mb-10">
          <h3 className="text-base font-semibold text-gray-900 mb-6">Background</h3>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Normal text</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Align</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">•</button>
              <button className="p-1 hover:bg-gray-200 rounded">1.</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="2" y="3" width="10" height="8" rx="1" stroke="#6B7280" strokeWidth="1.2"/>
                  <circle cx="4.5" cy="5.5" r="0.875" stroke="#6B7280" strokeWidth="1.2"/>
                  <path d="M12 9L9.5 6.5L6 10" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <textarea
              rows={6}
              className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
              placeholder=""
            />
          </div>
        </div>

        {/* Scoring System */}
        <div className="mb-10">
          <h3 className="text-base font-semibold text-gray-900 mb-6">Scoring System</h3>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Normal text</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Align</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">•</button>
              <button className="p-1 hover:bg-gray-200 rounded">1.</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="2" y="3" width="10" height="8" rx="1" stroke="#6B7280" strokeWidth="1.2"/>
                  <circle cx="4.5" cy="5.5" r="0.875" stroke="#6B7280" strokeWidth="1.2"/>
                  <path d="M12 9L9.5 6.5L6 10" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <textarea
              rows={6}
              className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
              placeholder=""
            />
          </div>
        </div>

        {/* Development Methodology */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Development Methodology</h3>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Normal text</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Align</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">•</button>
              <button className="p-1 hover:bg-gray-200 rounded">1.</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="2" y="3" width="10" height="8" rx="1" stroke="#6B7280" strokeWidth="1.2"/>
                  <circle cx="4.5" cy="5.5" r="0.875" stroke="#6B7280" strokeWidth="1.2"/>
                  <path d="M12 9L9.5 6.5L6 10" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <textarea
              rows={6}
              className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
              placeholder=""
            />
          </div>
        </div>

        {/* Example Studies */}
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Example Studies</h3>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 6L7 4L9 6" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9 8L7 10L5 8" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Normal text</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <select className="text-xs border-0 bg-transparent py-1 focus:outline-none">
                <option>Align</option>
              </select>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded text-xs font-bold">B</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs italic">I</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs underline">U</button>
              <button className="p-1 hover:bg-gray-200 rounded text-xs line-through">S</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">•</button>
              <button className="p-1 hover:bg-gray-200 rounded">1.</button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button className="p-1 hover:bg-gray-200 rounded">🔗</button>
              <button className="p-1 hover:bg-gray-200 rounded">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="2" y="3" width="10" height="8" rx="1" stroke="#6B7280" strokeWidth="1.2"/>
                  <circle cx="4.5" cy="5.5" r="0.875" stroke="#6B7280" strokeWidth="1.2"/>
                  <path d="M12 9L9.5 6.5L6 10" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <textarea
              rows={6}
              className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
              placeholder=""
            />
          </div>
        </div>
          </>
        )}

        {/* Documents Tab */}
        {activeTab === 'Documents' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg font-semibold text-gray-900">Support Documents List</h2>
              <button 
                onClick={() => setIsAddDocumentModalOpen(true)}
                className="px-6 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
              >
                Add Document
              </button>
            </div>

            {/* Documents Table using DataTable component */}
            <DataTable
              columns={[
                { header: 'Document Name', accessor: 'name' },
                { header: 'Document Detail', accessor: 'detail', className: 'max-w-xs truncate' },
                { header: 'Fee', accessor: 'fee', className: 'font-semibold' },
                { header: 'Date Created', accessor: 'dateCreated' },
                { header: 'Last Action', accessor: 'lastAction' },
                { header: 'Last Action by', accessor: 'user' }
              ]}
              data={documents}
              rowsPerPage={documents.length || 1000}
              renderCell={(accessor, row, column) => {
                if (accessor === 'user') {
                  return (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-400 text-white text-xs flex items-center justify-center font-medium">
                        {row.avatar}
                      </div>
                      <span className="text-sm text-gray-900">{row.user}</span>
                    </div>
                  );
                }
                return (
                  <span className={`text-sm ${column.className || 'text-gray-900'}`}>
                    {row[accessor]}
                  </span>
                );
              }}
              actionMenuItems={[
                { 
                  label: 'Edit Document', 
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M9.16667 1.66667H7.5C3.33333 1.66667 1.66667 3.33334 1.66667 7.5V12.5C1.66667 16.6667 3.33333 18.3333 7.5 18.3333H12.5C16.6667 18.3333 18.3333 16.6667 18.3333 12.5V10.8333" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M13.3667 2.51667L6.80001 9.08334C6.55001 9.33334 6.30001 9.825 6.25001 10.1833L5.89168 12.6917C5.75834 13.6 6.40001 14.2333 7.30834 14.1083L9.81668 13.75C10.1667 13.7 10.6583 13.45 10.9167 13.2L17.4833 6.63334C18.6167 5.5 19.15 4.18334 17.4833 2.51667C15.8167 0.850002 14.5 1.38334 13.3667 2.51667Z" stroke="#73827c" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ), 
                  action: 'edit',
                  danger: false
                },
                { 
                  label: 'View Document', 
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M12.9833 10C12.9833 11.65 11.65 12.9833 10 12.9833C8.35 12.9833 7.01666 11.65 7.01666 10C7.01666 8.35 8.35 7.01666 10 7.01666C11.65 7.01666 12.9833 8.35 12.9833 10Z" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9.99999 16.8917C12.9417 16.8917 15.6833 15.1583 17.5917 12.1583C18.3417 10.9833 18.3417 9.00833 17.5917 7.83333C15.6833 4.83333 12.9417 3.1 9.99999 3.1C7.05832 3.1 4.31666 4.83333 2.40832 7.83333C1.65832 9.00833 1.65832 10.9833 2.40832 12.1583C4.31666 15.1583 7.05832 16.8917 9.99999 16.8917Z" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ), 
                  action: 'view',
                  danger: false
                },
                { 
                  label: 'Delete', 
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M17.5 4.98333C14.725 4.70833 11.9333 4.56667 9.15 4.56667C7.5 4.56667 5.85 4.65 4.2 4.81667L2.5 4.98333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7.08334 4.14167L7.26668 3.05C7.40001 2.25833 7.50001 1.66667 8.90834 1.66667H11.0917C12.5 1.66667 12.6083 2.29167 12.7333 3.05833L12.9167 4.14167" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15.7083 7.61667L15.1667 16.0083C15.075 17.3167 15 18.3333 12.675 18.3333H7.32499C4.99999 18.3333 4.92499 17.3167 4.83332 16.0083L4.29166 7.61667" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8.60834 13.75H11.3833" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7.91666 10.4167H12.0833" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ), 
                  action: 'delete',
                  danger: true
                }
              ]}
              onRowAction={handleDocumentAction}
              showPagination={false}
            />
          </>
        )}

        {/* Translations Tab */}
        {activeTab === 'Translations' && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Translations</h2>
              <button
                onClick={() => setIsAddTranslationModalOpen(true)}
                className="px-5 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm flex items-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3.33334V12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.33334 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add Translation
              </button>
            </div>

            {/* Translations Table */}
            <DataTable
              columns={[
                { header: 'Country', accessor: 'country' },
                { header: 'Language', accessor: 'language' },
                { header: 'Status', accessor: 'status' },
                { header: 'Tag', accessor: 'tag' },
                { header: 'Fee', accessor: 'fee' },
                { header: 'Note', accessor: 'note' },
                { header: 'Date Created', accessor: 'dateCreated' },
                { header: 'Last Action', accessor: 'lastAction' },
                { header: 'Last Action by', accessor: 'lastActionBy' }
              ]}
              data={translations}
              rowsPerPage={translations.length || 1000}
              renderCell={(accessor, row, column) => {
                if (accessor === 'status') {
                  const statusConfig = {
                    approved: { 
                      bg: 'bg-[#E8F5E9]', 
                      text: 'text-[#2E7D32]', 
                      border: 'border-[#81C784]', 
                      dot: 'bg-[#4CAF50]',
                      label: 'Approved' 
                    },
                    pending: { 
                      bg: 'bg-[#FFF3E0]', 
                      text: 'text-[#E65100]', 
                      border: 'border-[#FFB74D]', 
                      dot: 'bg-[#FF9800]',
                      label: 'Pending' 
                    },
                    rejected: { 
                      bg: 'bg-[#FFEBEE]', 
                      text: 'text-[#C62828]', 
                      border: 'border-[#E57373]', 
                      dot: 'bg-[#F44336]',
                      label: 'Rejected' 
                    }
                  };
                  const config = statusConfig[row.status] || statusConfig.pending;
                  return (
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
                      {config.label}
                    </span>
                  );
                }
                if (accessor === 'tag') {
                  const tagConfig = {
                    'IHO Commissioned': { color: 'text-[#7CB342]' },
                    'Translate Themselves': { color: 'text-[#FF9800]' },
                    'Form Library': { color: 'text-[#E53935]' }
                  };
                  const tagLabel = { 'new Coa': 'New COA' };
                  const config = tagConfig[row.tag] || { color: 'text-gray-600' };
                  return (
                    <span className={`text-xs font-normal ${config.color}`}>
                      {tagLabel[row.tag] ?? row.tag}
                    </span>
                  );
                }
                if (accessor === 'lastActionBy') {
                  return (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-400 text-white text-xs flex items-center justify-center font-medium">
                        {row.avatar}
                      </div>
                      <span className="text-sm text-gray-900">{row.lastActionBy}</span>
                    </div>
                  );
                }
                if (accessor === 'fee') {
                  return (
                    <span className="text-sm text-gray-600">
                      {row[accessor]}
                    </span>
                  );
                }
                if (accessor === 'note') {
                  return (
                    <span className="text-sm text-gray-500 max-w-xs truncate block">
                      {row[accessor]}
                    </span>
                  );
                }
                return (
                  <span className={`text-sm ${column.className || 'text-gray-900'}`}>
                    {row[accessor]}
                  </span>
                );
              }}
              actionMenuItems={[
                { 
                  label: 'Edit Translation', 
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M14.1667 2.49998C14.3856 2.28113 14.6454 2.10744 14.9314 1.98899C15.2173 1.87054 15.5238 1.80957 15.8334 1.80957C16.1429 1.80957 16.4494 1.87054 16.7353 1.98899C17.0213 2.10744 17.2811 2.28113 17.5 2.49998C17.7189 2.71884 17.8926 2.97863 18.011 3.2646C18.1295 3.55057 18.1904 3.85706 18.1904 4.16665C18.1904 4.47624 18.1295 4.78273 18.011 5.0687C17.8926 5.35467 17.7189 5.61446 17.5 5.83332L6.25002 17.0833L1.66669 18.3333L2.91669 13.75L14.1667 2.49998Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ), 
                  action: 'edit'
                },
                { 
                  label: 'View Translation', 
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M0.833344 9.99998C0.833344 9.99998 4.16668 3.33331 10 3.33331C15.8333 3.33331 19.1667 9.99998 19.1667 9.99998C19.1667 9.99998 15.8333 16.6666 10 16.6666C4.16668 16.6666 0.833344 9.99998 0.833344 9.99998Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#485B54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ), 
                  action: 'view'
                },
                { 
                  label: 'Delete', 
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M2.5 5H4.16667H17.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15.8333 4.99999V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83332C5.39129 18.3333 4.96737 18.1577 4.65481 17.8452C4.34225 17.5326 4.16666 17.1087 4.16666 16.6667V4.99999M6.66666 4.99999V3.33332C6.66666 2.89129 6.84225 2.46737 7.15481 2.15481C7.46737 1.84225 7.89129 1.66666 8.33332 1.66666H11.6667C12.1087 1.66666 12.5326 1.84225 12.8452 2.15481C13.1577 2.46737 13.3333 2.89129 13.3333 3.33332V4.99999" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8.33334 9.16666V14.1667" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M11.6667 9.16666V14.1667" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ), 
                  action: 'delete',
                  className: 'text-red-600 hover:bg-red-50'
                }
              ]}
              onRowAction={handleTranslationAction}
              showPagination={false}
            />
          </>
        )}

        {/* Fee Tab */}
        {activeTab === 'Fee' && (
          <>
            {/* Header with Edit Button */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl font-semibold text-gray-900">Fee</h2>
                <button 
                  onClick={() => setIsAddFeeTypeModalOpen(true)}
                  className="flex items-center justify-center w-9 h-9 rounded-full border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-50 transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3.33334V12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3.33334 8H12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <button
                className="px-5 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
              >
                Edit Table
              </button>
            </div>

            {/* Fee Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[1400px]">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10">Fee Type</th>
                      {ALL_ORGANIZATION_TYPES.map((orgType) => (
                        <th key={orgType.key} className="px-4 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                          <div>{orgType.shortLabel}</div>
                        </th>
                      ))}
                    </tr>
                </thead>
                <tbody>
                  {feeData.feeTypes && feeData.feeTypes.map((feeType, index) => (
                    <tr key={feeType.name} className={`${index !== feeData.feeTypes.length - 1 ? 'border-b border-gray-200' : ''} hover:bg-gray-50`}>
                      <td className="px-6 py-4 text-sm text-gray-700 font-medium sticky left-0 bg-white z-10">{feeType.name}</td>
                      {ALL_ORGANIZATION_TYPES.map((orgType) => (
                        <td key={orgType.key} className="px-4 py-4 text-center">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={feeType[orgType.key] || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                              const updatedFeeTypes = feeData.feeTypes.map(ft => 
                                ft.name === feeType.name 
                                  ? { ...ft, [orgType.key]: value }
                                  : ft
                              );
                              setFeeData({ ...feeData, feeTypes: updatedFeeTypes });
                            }}
                            className="w-full max-w-[120px] px-3 py-2 text-sm text-center text-gray-900 bg-transparent border border-transparent hover:border-gray-300 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded transition-all mx-auto"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}

        {/* FAQ's Tab */}
        {activeTab === "FAQ's" && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">FAQ's</h2>
              <button
                onClick={() => setIsAddFAQModalOpen(true)}
                className="px-5 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
              >
                Add FAQ
              </button>
            </div>

            {/* FAQ Items (sorted by display order) */}
            <div className="space-y-6">
              {[...faqData].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)).map((faq) => (
                <div key={faq._id || faq.id} className="bg-white rounded-xl border border-gray-200 overflow-visible">
                  <div className="flex items-start justify-between px-6 py-5 gap-4">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Order {faq.order ?? 0}</p>
                      <h3 className="text-base font-semibold text-gray-900 mb-3">{faq.question}</h3>
                      {faq.isList ? (
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
                          {faq.answer.split('\n').map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                      )}
                    </div>
                    <ActionMenu 
                      items={faqMenuItems}
                      onAction={handleFAQAction}
                      itemId={faq._id || faq.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Terms & Conditions Tab */}
        {activeTab === 'Terms & Conditions' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Terms & Conditions</h2>
            </div>

            {/* Rich Text Editor */}
            <RichTextEditor
              value={termsContent}
              onChange={setTermsContent}
              placeholder="Start typing your terms and conditions..."
            />
          </>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
        <button
          onClick={() => {
            const currentIndex = tabs.indexOf(activeTab);
            if (currentIndex > 0) {
              setActiveTab(tabs[currentIndex - 1]);
            } else {
              navigate(-1);
            }
          }}
          className="px-6 py-2.5 text-gray-600 text-sm font-medium hover:text-gray-900 transition-colors"
        >
          Back
        </button>
        <button 
          onClick={() => {
            const currentIndex = tabs.indexOf(activeTab);
            if (currentIndex < tabs.length - 1) {
              setActiveTab(tabs[currentIndex + 1]);
            } else {
              // Submit logic here
              navigate('/requested-licences');
            }
          }}
          className="px-6 py-2.5 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
        >
          {activeTab === 'Terms & Conditions' ? 'Submit' : 'Continue'}
        </button>
      </div>
    </div>
    </>
  );
};

export default EditLicence;



